import React, { useState, useEffect, useCallback } from 'react';
import { ShoppingBag, Search, ChevronLeft, ChevronRight, RefreshCw, Tag, Plus, X, Phone, Loader2, CheckCircle, XCircle, Clock, User, ChevronDown, ChevronUp, MessageSquare, Send, MessageCircle } from 'lucide-react';
import { apiRequest } from '../utils/api-request';

interface Order {
  order_id: number;
  uuid: string;
  shopify_order_id: string;
  order_number: string;
  customer_name: string;
  email: string;
  phone: string;
  total_price: string;
  currency: string;
  financial_status: string;
  fulfillment_status: string;
  order_status: string;
  tags: string;
  source_name: string;
  order_date: string;
  owner_name: string | null;
  assigned_at: string | null;
  latest_delivery_status?: number | null;
  latest_delivery_status_desc?: string | null;
}

interface Pagination {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

interface OrdersResponse {
  orders: Order[];
  pagination: Pagination;
}

const ITEMS_PER_PAGE = 20;

const getDeliveryStatusBadge = (statusCode: number | null | undefined): { label: string; className: string } => {
  switch (statusCode) {
    case 8: case 48: return { label: 'Delivered', className: 'bg-green-100 text-green-800' };
    case 6:  return { label: 'Out for Delivery', className: 'bg-blue-100 text-blue-800' };
    case 4:  return { label: 'Picked Up', className: 'bg-indigo-100 text-indigo-800' };
    case 9:  return { label: 'Failed Delivery', className: 'bg-red-100 text-red-800' };
    case 14: return { label: 'RTO Delivered', className: 'bg-orange-100 text-orange-800' };
    case 12: case 21: return { label: 'RTO', className: 'bg-orange-100 text-orange-800' };
    case 5:  return { label: 'In Transit', className: 'bg-yellow-100 text-yellow-800' };
    default: return statusCode != null
      ? { label: 'In Transit', className: 'bg-yellow-100 text-yellow-800' }
      : { label: '', className: '' };
  }
};

interface OutboundConfig {
  is_active: boolean;
  provider: string;
}

interface ActivityLog {
  id: number;
  uuid: string;
  activity_type: string;
  activity_data: Record<string, any>;
  performed_by: string;
  performed_by_user_id: number | null;
  created_at: string;
}

interface WhatsAppTemplate {
  id: string;
  name: string;
  description: string;
  parameters: string[];
  sample: string;
}

const OrdersProcessing: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortColumn, setSortColumn] = useState('order_date');
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('DESC');
  const [tagModalOrder, setTagModalOrder] = useState<Order | null>(null);
  const [newTagInput, setNewTagInput] = useState('');
  const [pendingTags, setPendingTags] = useState<string[]>([]);
  const [isSubmittingTags, setIsSubmittingTags] = useState(false);
  const [activityLogs, setActivityLogs] = useState<ActivityLog[]>([]);
  const [isLoadingLogs, setIsLoadingLogs] = useState(false);
  const [showActivityLogs, setShowActivityLogs] = useState(false);
  // Remarks modal state
  const [remarkModalOrder, setRemarkModalOrder] = useState<Order | null>(null);
  const [remarkInput, setRemarkInput] = useState('');
  const [isAddingRemark, setIsAddingRemark] = useState(false);
  // WhatsApp modal state
  const [whatsAppModalOrder, setWhatsAppModalOrder] = useState<Order | null>(null);
  const [whatsAppTemplates, setWhatsAppTemplates] = useState<WhatsAppTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<WhatsAppTemplate | null>(null);
  const [templateParams, setTemplateParams] = useState<Record<string, string>>({});
  const [isSendingWhatsApp, setIsSendingWhatsApp] = useState(false);
  const [outboundConfig, setOutboundConfig] = useState<OutboundConfig | null>(null);
  const [callModalOrder, setCallModalOrder] = useState<Order | null>(null);
  const [callState, setCallState] = useState<'initiating' | 'success' | 'error'>('initiating');
  const [callErrorMessage, setCallErrorMessage] = useState<string>('');

  const loadOrders = useCallback(async (page: number = 1, search: string = '', sort: string = sortColumn, order: 'ASC' | 'DESC' = sortOrder) => {
    setIsLoading(true);
    try {
      const response = await apiRequest<OrdersResponse>('/shopify/orders/processing', {
        method: 'POST',
        body: JSON.stringify({
          page,
          limit: ITEMS_PER_PAGE,
          search_term: search,
          sort_column: sort,
          sort_order: order,
          days_back: 7
        })
      });

      if (response.success && response.data) {
        setOrders(response.data.orders);
        setPagination(response.data.pagination);
        setCurrentPage(response.data.pagination.page);
      }
    } catch (error) {
      console.error('Failed to load orders:', error);
    } finally {
      setIsLoading(false);
    }
  }, [sortColumn, sortOrder]);

  useEffect(() => {
    loadOrders(1, searchTerm);
    loadOutboundConfig();
  }, []);

  const loadOutboundConfig = async () => {
    try {
      const response = await apiRequest<{ is_active: boolean; provider: string }>('/api/outbound-call/config');
      if (response.success && response.data) {
        setOutboundConfig(response.data);
      }
    } catch (error) {
      console.error('Failed to load outbound config:', error);
    }
  };

  const openCallModal = (order: Order) => {
    setCallModalOrder(order);
    setCallState('initiating');
    setCallErrorMessage('');
    initiateCall(order);
  };

  const closeCallModal = () => {
    setCallModalOrder(null);
    setCallState('initiating');
    setCallErrorMessage('');
  };

  const initiateCall = async (order: Order) => {
    try {
      setCallState('initiating');
      setCallErrorMessage('');

      const response = await apiRequest<{ call_uuid: string }>('/api/outbound-call/call/order', {
        method: 'POST',
        body: JSON.stringify({
          order_uuid: order.uuid,
          customer_name: order.customer_name,
          customer_phone: order.phone
        })
      });

      if (response.success) {
        setCallState('success');
        // Auto-close after 3 seconds on success
        setTimeout(() => {
          closeCallModal();
        }, 3000);
      } else {
        setCallState('error');
        setCallErrorMessage(response.message || 'Failed to initiate call');
      }
    } catch (error: any) {
      setCallState('error');
      setCallErrorMessage(error.message || 'Failed to initiate call');
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchTerm(searchInput);
    loadOrders(1, searchInput);
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearchTerm('');
    loadOrders(1, '');
  };

  const handlePageChange = (page: number) => {
    loadOrders(page, searchTerm);
  };

  const handleSort = (column: string) => {
    const newOrder = sortColumn === column && sortOrder === 'DESC' ? 'ASC' : 'DESC';
    setSortColumn(column);
    setSortOrder(newOrder);
    loadOrders(currentPage, searchTerm, column, newOrder);
  };

  const handleRefresh = () => {
    loadOrders(currentPage, searchTerm);
  };

  // Soft add tag to pending list (no API call)
  const handleAddTag = (tagName: string) => {
    if (!tagModalOrder) return;

    const trimmedTag = tagName.trim();
    if (!trimmedTag) return;

    // Check if tag already exists in current tags or pending tags
    const existingTags = tagModalOrder.tags ? tagModalOrder.tags.split(',').map(t => t.trim().toLowerCase()) : [];
    const pendingLower = pendingTags.map(t => t.toLowerCase());

    if (existingTags.includes(trimmedTag.toLowerCase()) || pendingLower.includes(trimmedTag.toLowerCase())) {
      return; // Tag already exists
    }

    setPendingTags(prev => [...prev, trimmedTag]);
    setNewTagInput('');
  };

  // Remove tag from pending list
  const handleRemovePendingTag = (tagToRemove: string) => {
    setPendingTags(prev => prev.filter(t => t !== tagToRemove));
  };

  // Submit all pending tags to API
  const handleSubmitTags = async () => {
    if (!tagModalOrder || pendingTags.length === 0 || isSubmittingTags) return;

    setIsSubmittingTags(true);
    try {
      // Add tags one by one (API accepts single tag)
      let updatedTags = tagModalOrder.tags || '';

      for (const tag of pendingTags) {
        const response = await apiRequest<{ success: boolean; message?: string; tags?: string }>('/shopify/orders/add-tag', {
          method: 'POST',
          body: JSON.stringify({
            shopify_order_id: tagModalOrder.shopify_order_id,
            order_id: tagModalOrder.order_id,
            tag: tag
          })
        });

        if (response.success && response.data?.tags) {
          updatedTags = response.data.tags;
        }
      }

      // Update the order in the local state
      setOrders(prevOrders =>
        prevOrders.map(o =>
          o.order_id === tagModalOrder.order_id
            ? { ...o, tags: updatedTags }
            : o
        )
      );
      setTagModalOrder(prev => prev ? { ...prev, tags: updatedTags } : null);
      setPendingTags([]);
      closeTagModal();
    } catch (error) {
      console.error('Failed to add tags:', error);
      alert('Failed to add tags');
    } finally {
      setIsSubmittingTags(false);
    }
  };

  const loadActivityLogs = async (orderId: number) => {
    setIsLoadingLogs(true);
    try {
      const response = await apiRequest<ActivityLog[]>(`/shopify/orders/${orderId}/activity-logs`);
      if (response.success && response.data) {
        setActivityLogs(response.data);
      }
    } catch (error) {
      console.error('Failed to load activity logs:', error);
    } finally {
      setIsLoadingLogs(false);
    }
  };

  const loadWhatsAppTemplates = async () => {
    try {
      const response = await apiRequest<WhatsAppTemplate[]>('/shopify/whatsapp/templates');
      if (response.success && response.data) {
        setWhatsAppTemplates(response.data);
      }
    } catch (error) {
      console.error('Failed to load WhatsApp templates:', error);
    }
  };

  const handleAddRemark = async () => {
    if (!remarkModalOrder || !remarkInput.trim() || isAddingRemark) return;

    setIsAddingRemark(true);
    try {
      const response = await apiRequest<{ success: boolean; message?: string }>('/shopify/orders/add-remark', {
        method: 'POST',
        body: JSON.stringify({
          order_id: remarkModalOrder.order_id,
          shopify_order_id: remarkModalOrder.shopify_order_id,
          remark: remarkInput.trim()
        })
      });

      if (response.success) {
        setRemarkInput('');
        closeRemarkModal();
      } else {
        alert(response.message || 'Failed to add remark');
      }
    } catch (error) {
      console.error('Failed to add remark:', error);
      alert('Failed to add remark');
    } finally {
      setIsAddingRemark(false);
    }
  };

  const handleSendWhatsApp = async () => {
    if (!whatsAppModalOrder || !selectedTemplate || isSendingWhatsApp) return;

    if (!whatsAppModalOrder.phone) {
      alert('No phone number available for this order');
      return;
    }

    setIsSendingWhatsApp(true);
    try {
      // Build parameters array from the filled form
      const parameters = selectedTemplate.parameters.map(param => templateParams[param] || '');

      const response = await apiRequest<{ success: boolean; message?: string }>('/shopify/orders/send-whatsapp', {
        method: 'POST',
        body: JSON.stringify({
          order_id: whatsAppModalOrder.order_id,
          shopify_order_id: whatsAppModalOrder.shopify_order_id,
          phone: whatsAppModalOrder.phone,
          template_id: selectedTemplate.id,
          template_name: selectedTemplate.name,
          language: 'en',
          parameters
        })
      });

      if (response.success) {
        alert('WhatsApp message sent successfully!');
        closeWhatsAppModal();
      } else {
        alert(response.message || 'Failed to send WhatsApp message');
      }
    } catch (error) {
      console.error('Failed to send WhatsApp message:', error);
      alert('Failed to send WhatsApp message');
    } finally {
      setIsSendingWhatsApp(false);
    }
  };

  const prefillTemplateParams = (template: WhatsAppTemplate, order: Order) => {
    const params: Record<string, string> = {};
    template.parameters.forEach(param => {
      switch (param) {
        case 'customer_name':
          params[param] = order.customer_name || '';
          break;
        case 'order_number':
          params[param] = order.order_number || '';
          break;
        case 'amount':
          params[param] = order.total_price || '';
          break;
        default:
          params[param] = '';
      }
    });
    return params;
  };

  // Tag Modal
  const openTagModal = (order: Order) => {
    setTagModalOrder(order);
    setNewTagInput('');
    setPendingTags([]);
    setActivityLogs([]);
    setShowActivityLogs(false);
    loadActivityLogs(order.order_id);
  };

  const closeTagModal = () => {
    setTagModalOrder(null);
    setNewTagInput('');
    setPendingTags([]);
    setActivityLogs([]);
    setShowActivityLogs(false);
  };

  // Remark Modal
  const openRemarkModal = (order: Order) => {
    setRemarkModalOrder(order);
    setRemarkInput('');
  };

  const closeRemarkModal = () => {
    setRemarkModalOrder(null);
    setRemarkInput('');
  };

  // WhatsApp Modal
  const openWhatsAppModal = (order: Order) => {
    setWhatsAppModalOrder(order);
    setSelectedTemplate(null);
    setTemplateParams({});
    loadWhatsAppTemplates();
  };

  const closeWhatsAppModal = () => {
    setWhatsAppModalOrder(null);
    setSelectedTemplate(null);
    setTemplateParams({});
  };

  const formatActivityType = (type: string): string => {
    const typeMap: Record<string, string> = {
      'TAG_ADDED': 'Tag Added',
      'REMARK_ADDED': 'Remark Added',
      'PAYMENT_LINK_SENT': 'Payment Link Sent',
      'CALL_INITIATED': 'Call Initiated',
      'STATUS_CHANGED': 'Status Changed',
      'ORDER_ASSIGNED': 'Order Assigned',
      'ORDER_CREATED': 'Order Created',
      'OTHER': 'Other'
    };
    return typeMap[type] || type;
  };

  const formatActivityData = (type: string, data: Record<string, any>): string => {
    switch (type) {
      case 'TAG_ADDED':
        return `"${data.tag}"`;
      case 'REMARK_ADDED':
        return data.remark?.substring(0, 50) + (data.remark?.length > 50 ? '...' : '');
      case 'PAYMENT_LINK_SENT':
        return `via ${data.channel || 'unknown'}`;
      case 'CALL_INITIATED':
        return `to ${data.phone || 'customer'}`;
      default:
        return '';
    }
  };

  const formatLogDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const formatted = date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
    // Convert am/pm to AM/PM
    return formatted.replace(/\bam\b/gi, 'AM').replace(/\bpm\b/gi, 'PM');
  };

  const formatCurrency = (amount: string | number) => {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    return '₹' + new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(num);
  };

  const getStatusBadge = (status: string, type: 'financial' | 'fulfillment') => {
    if (type === 'financial') {
      switch (status) {
        case 'paid':
          return 'bg-green-100 text-green-800';
        case 'refunded':
          return 'bg-red-100 text-red-800';
        case 'pending':
          return 'bg-yellow-100 text-yellow-800';
        default:
          return 'bg-gray-100 text-gray-800';
      }
    } else {
      switch (status) {
        case 'fulfilled':
          return 'bg-green-100 text-green-800';
        case 'partial':
          return 'bg-yellow-100 text-yellow-800';
        default:
          return 'bg-gray-100 text-gray-800';
      }
    }
  };

  const SortIcon = ({ column }: { column: string }) => {
    if (sortColumn !== column) return null;
    return (
      <span className="ml-1">
        {sortOrder === 'ASC' ? '↑' : '↓'}
      </span>
    );
  };

  const renderTags = (tags: string, maxTags: number = 2) => {
    if (!tags) return <span className="text-gray-400">-</span>;
    const tagList = tags.split(',').map(t => t.trim()).filter(t => t);
    if (tagList.length === 0) return <span className="text-gray-400">-</span>;

    const visibleTags = tagList.slice(0, maxTags);
    const remainingCount = tagList.length - maxTags;

    return (
      <div className="flex flex-wrap gap-1 items-center">
        {visibleTags.map((tag, index) => (
          <span
            key={index}
            className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800"
          >
            {tag}
          </span>
        ))}
        {remainingCount > 0 && (
          <span className="text-xs text-gray-500 font-medium">+{remainingCount}</span>
        )}
      </div>
    );
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between gap-6">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-brandColor rounded-lg text-white">
              <ShoppingBag className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-lg font-semibold text-gray-900">
                Orders Processing
              </h1>
              <p className="text-xs text-gray-500">
                Orders from the past 7 days
              </p>
            </div>
          </div>

          {/* Search Form - Right aligned */}
          <div className="flex items-center gap-2">
            <form onSubmit={handleSearch} className="flex gap-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Search by order #, name, phone, email, or tags..."
                  className="w-80 pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-brandColor"
                />
              </div>
              <button
                type="submit"
                className="px-4 py-2 bg-brandColor text-white rounded-lg hover:opacity-90"
              >
                Search
              </button>
              {searchTerm && (
                <button
                  type="button"
                  onClick={handleClearSearch}
                  className="px-4 py-2 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50"
                >
                  Clear
                </button>
              )}
            </form>

            {/* Refresh Button */}
            <button
              onClick={handleRefresh}
              disabled={isLoading}
              className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-50"
              title="Refresh orders"
            >
              <RefreshCw className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      {/* Table Content */}
      <div className="flex-1 overflow-hidden p-6 bg-gray-50">
        {isLoading && orders.length === 0 ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brandColor"></div>
            <span className="ml-2 text-gray-600">Loading orders...</span>
          </div>
        ) : orders.length === 0 ? (
          <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
            <ShoppingBag className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No orders found</h3>
            <p className="text-sm text-gray-500">
              {searchTerm ? 'Try adjusting your search query' : 'No orders in the past 7 days'}
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-gray-300 flex flex-col h-full overflow-hidden">
            <div className="overflow-auto h-full">
              <table className="min-w-full border-collapse">
                <thead className="bg-brandColor sticky top-0 z-10">
                  <tr>
                    <th
                      className="px-2 py-2 text-left text-xs font-medium text-white uppercase cursor-pointer hover:bg-brandColor/90 border-r border-brandColor/50 whitespace-nowrap"
                      onClick={() => handleSort('order_number')}
                    >
                      Order # <SortIcon column="order_number" />
                    </th>
                    <th
                      className="px-2 py-2 text-left text-xs font-medium text-white uppercase cursor-pointer hover:bg-brandColor/90 border-r border-brandColor/50 whitespace-nowrap"
                      onClick={() => handleSort('customer_name')}
                    >
                      Customer <SortIcon column="customer_name" />
                    </th>
                    <th className="px-2 py-2 text-left text-xs font-medium text-white uppercase border-r border-brandColor/50 whitespace-nowrap">
                      Phone
                    </th>
                    <th className="px-2 py-2 text-left text-xs font-medium text-white uppercase border-r border-brandColor/50 whitespace-nowrap">
                      Owner
                    </th>
                    <th className="px-2 py-2 text-left text-xs font-medium text-white uppercase border-r border-brandColor/50 whitespace-nowrap min-w-[180px]">
                      Tags
                    </th>
                    <th
                      className="px-2 py-2 text-left text-xs font-medium text-white uppercase cursor-pointer hover:bg-brandColor/90 border-r border-brandColor/50 whitespace-nowrap"
                      onClick={() => handleSort('total_price')}
                    >
                      Amount <SortIcon column="total_price" />
                    </th>
                    <th
                      className="px-2 py-2 text-left text-xs font-medium text-white uppercase cursor-pointer hover:bg-brandColor/90 border-r border-brandColor/50 whitespace-nowrap"
                      onClick={() => handleSort('financial_status')}
                    >
                      Payment <SortIcon column="financial_status" />
                    </th>
                    <th
                      className="px-2 py-2 text-left text-xs font-medium text-white uppercase cursor-pointer hover:bg-brandColor/90 border-r border-brandColor/50 whitespace-nowrap"
                      onClick={() => handleSort('fulfillment_status')}
                    >
                      Fulfillment <SortIcon column="fulfillment_status" />
                    </th>
                    <th
                      className="px-2 py-2 text-left text-xs font-medium text-white uppercase cursor-pointer hover:bg-brandColor/90 whitespace-nowrap"
                      onClick={() => handleSort('order_date')}
                    >
                      Date <SortIcon column="order_date" />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order) => (
                    <tr key={order.uuid} className="hover:bg-gray-50 border-b border-gray-200">
                      <td className="px-2 py-1.5 whitespace-nowrap border-r border-gray-200">
                        <span className="text-sm font-medium text-gray-900">#ZH{order.order_number}</span>
                      </td>
                      <td className="px-2 py-1.5 whitespace-nowrap border-r border-gray-200">
                        <span className="text-sm text-gray-900">{order.customer_name}</span>
                      </td>
                      <td className="px-2 py-1.5 whitespace-nowrap text-sm text-gray-900 border-r border-gray-200">
                        <div className="flex items-center justify-between gap-2">
                          <span>{order.phone}</span>
                          <div className="flex items-center gap-1">
                            {outboundConfig?.is_active && order.phone && (
                              <button
                                onClick={() => openCallModal(order)}
                                className="p-1 rounded transition-colors text-gray-500 hover:bg-green-100 hover:text-green-600"
                                title="Click to call"
                              >
                                <Phone className="w-3.5 h-3.5" />
                              </button>
                            )}
                            <button
                              onClick={() => openRemarkModal(order)}
                              className="p-1 rounded transition-colors text-gray-500 hover:bg-blue-100 hover:text-blue-600"
                              title="Add Remark"
                            >
                              <MessageSquare className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => openWhatsAppModal(order)}
                              className="p-1 rounded transition-colors text-gray-500 hover:bg-green-100 hover:text-green-600"
                              title="Send WhatsApp"
                            >
                              <MessageCircle className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      </td>
                      <td className="px-2 py-1.5 whitespace-nowrap border-r border-gray-200">
                        {order.owner_name ? (
                          <span className="text-sm text-gray-900">{order.owner_name}</span>
                        ) : (
                          <span className="text-sm text-gray-400 italic">Unassigned</span>
                        )}
                      </td>
                      <td className="px-2 py-1.5 border-r border-gray-200">
                        <div className="flex items-center justify-between gap-1.5">
                          <div className="flex-1">{renderTags(order.tags)}</div>
                          <button
                            onClick={() => openTagModal(order)}
                            className="flex-shrink-0 p-1 text-white bg-brandColor hover:opacity-90 rounded"
                            title="Add Tag"
                          >
                            <Plus className="w-3 h-3" />
                          </button>
                        </div>
                      </td>
                      <td className="px-2 py-1.5 whitespace-nowrap text-sm font-medium text-gray-900 border-r border-gray-200">
                        {formatCurrency(order.total_price)}
                      </td>
                      <td className="px-2 py-1.5 whitespace-nowrap border-r border-gray-200">
                        <span className={`px-1.5 py-0.5 text-xs rounded-full ${getStatusBadge(order.financial_status, 'financial')}`}>
                          {order.financial_status}
                        </span>
                      </td>
                      <td className="px-2 py-1.5 whitespace-nowrap border-r border-gray-200">
                        <div className="flex flex-col gap-0.5">
                          <span className={`px-1.5 py-0.5 text-xs rounded-full ${getStatusBadge(order.fulfillment_status, 'fulfillment')}`}>
                            {order.fulfillment_status || 'unfulfilled'}
                          </span>
                          {order.latest_delivery_status != null && (
                            <span className={`px-1.5 py-0.5 text-xs rounded-full font-medium ${getDeliveryStatusBadge(order.latest_delivery_status).className}`}>
                              {order.latest_delivery_status_desc || getDeliveryStatusBadge(order.latest_delivery_status).label}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-2 py-1.5 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(order.order_date)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Fixed Footer Pagination */}
      {pagination && pagination.totalPages > 0 && orders.length > 0 && (() => {
        // Calculate page numbers to show (5 at a time)
        const totalPages = pagination.totalPages;
        const maxVisible = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
        let endPage = Math.min(totalPages, startPage + maxVisible - 1);

        // Adjust start if we're near the end
        if (endPage - startPage + 1 < maxVisible) {
          startPage = Math.max(1, endPage - maxVisible + 1);
        }

        const pageNumbers = [];
        for (let i = startPage; i <= endPage; i++) {
          pageNumbers.push(i);
        }

        return (
          <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-3 flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Showing <span className="font-medium">{((currentPage - 1) * ITEMS_PER_PAGE) + 1}</span> to{' '}
              <span className="font-medium">{Math.min(currentPage * ITEMS_PER_PAGE, pagination.total)}</span> of{' '}
              <span className="font-medium">{pagination.total.toLocaleString()}</span> orders
            </div>
            <div className="flex items-center space-x-1">
              {/* First Page */}
              <button
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
                className="px-2 py-1 rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                title="First page"
              >
                «
              </button>
              {/* Previous 5 pages */}
              <button
                onClick={() => handlePageChange(Math.max(1, currentPage - 5))}
                disabled={currentPage === 1}
                className="px-2 py-1 rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center text-sm"
                title="Previous 5 pages"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>

              {/* Show ellipsis if there are pages before */}
              {startPage > 1 && (
                <span className="px-2 py-1 text-gray-500">...</span>
              )}

              {/* Page number buttons */}
              {pageNumbers.map(pageNum => (
                <button
                  key={pageNum}
                  onClick={() => handlePageChange(pageNum)}
                  className={`px-3 py-1 rounded-md border text-sm min-w-[36px] ${
                    pageNum === currentPage
                      ? 'bg-brandColor text-white border-brandColor'
                      : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {pageNum}
                </button>
              ))}

              {/* Show ellipsis if there are pages after */}
              {endPage < totalPages && (
                <span className="px-2 py-1 text-gray-500">...</span>
              )}

              {/* Next 5 pages */}
              <button
                onClick={() => handlePageChange(Math.min(totalPages, currentPage + 5))}
                disabled={currentPage === totalPages}
                className="px-2 py-1 rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center text-sm"
                title="Next 5 pages"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
              {/* Last Page */}
              <button
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
                className="px-2 py-1 rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                title="Last page"
              >
                »
              </button>
            </div>
          </div>
        );
      })()}

      {/* Add Tag Modal */}
      {tagModalOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            {/* Header with brand background */}
            <div className="flex items-center justify-between px-4 py-3 bg-brandColor rounded-t-lg">
              <h3 className="text-lg font-semibold text-white">
                Add Tags to #ZH{tagModalOrder.order_number}
              </h3>
              <button
                onClick={closeTagModal}
                className="text-white/80 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4">
              {/* Current Tags */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Current Tags
                </label>
                <div className="flex flex-wrap gap-1 min-h-[32px] p-2 bg-gray-50 rounded border border-gray-200">
                  {tagModalOrder.tags ? (
                    tagModalOrder.tags.split(',').map((tag, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800"
                      >
                        <Tag className="w-3 h-3 mr-1" />
                        {tag.trim()}
                      </span>
                    ))
                  ) : (
                    <span className="text-sm text-gray-400 italic">No tags</span>
                  )}
                </div>
              </div>

              {/* Pending Tags (soft added, not yet submitted) */}
              {pendingTags.length > 0 && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tags to Add <span className="text-gray-400 font-normal">(not saved yet)</span>
                  </label>
                  <div className="flex flex-wrap gap-1 min-h-[32px] p-2 bg-green-50 rounded border border-green-200">
                    {pendingTags.map((tag, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800"
                      >
                        <Tag className="w-3 h-3 mr-1" />
                        {tag}
                        <button
                          onClick={() => handleRemovePendingTag(tag)}
                          className="ml-1 text-green-600 hover:text-green-800"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Add Tag from Dropdown */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Tag to Add
                </label>
                {(() => {
                  const PREDEFINED_TAGS = [
                    { value: 'READY_TO_FULFILL', label: 'Ready to Fulfill' },
                    { value: 'PRIORITY', label: 'Priority' },
                    { value: 'CALLBACK', label: 'Callback Requested' },
                    { value: 'VERIFIED', label: 'Verified' },
                    { value: 'COD_CONFIRMED', label: 'COD Confirmed' },
                    { value: 'PAYMENT_PENDING', label: 'Payment Pending' },
                    { value: 'PAYMENT_RECEIVED', label: 'Payment Received' },
                    { value: 'ADDRESS_ISSUE', label: 'Address Issue' },
                    { value: 'CUSTOMER_NOT_REACHABLE', label: 'Customer Not Reachable' },
                    { value: 'CANCELLED_BY_CUSTOMER', label: 'Cancelled by Customer' },
                    { value: 'RETURN_REQUEST', label: 'Return Request' },
                    { value: 'REFUND_PROCESSED', label: 'Refund Processed' },
                    { value: 'HOLD', label: 'On Hold' },
                    { value: 'VIP_CUSTOMER', label: 'VIP Customer' },
                    { value: 'FIRST_ORDER', label: 'First Order' },
                    { value: 'REPEAT_CUSTOMER', label: 'Repeat Customer' },
                  ];

                  const existingTags = tagModalOrder.tags
                    ? tagModalOrder.tags.split(',').map(t => t.trim().toUpperCase())
                    : [];
                  const pendingTagsUpper = pendingTags.map(t => t.toUpperCase());

                  const availableTags = PREDEFINED_TAGS.filter(
                    tag => !existingTags.includes(tag.value) && !pendingTagsUpper.includes(tag.value)
                  );

                  return (
                    <div className="flex gap-2">
                      <select
                        value={newTagInput}
                        onChange={(e) => setNewTagInput(e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-brandColor text-sm"
                        disabled={isSubmittingTags || availableTags.length === 0}
                      >
                        <option value="">
                          {availableTags.length === 0 ? 'All tags added' : 'Choose a tag...'}
                        </option>
                        {availableTags.map(tag => (
                          <option key={tag.value} value={tag.value}>
                            {tag.label}
                          </option>
                        ))}
                      </select>
                      <button
                        onClick={() => {
                          if (newTagInput) {
                            handleAddTag(newTagInput);
                            setNewTagInput('');
                          }
                        }}
                        disabled={!newTagInput || isSubmittingTags}
                        className="px-4 py-2 bg-brandColor text-white rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                      >
                        <Plus className="w-4 h-4" />
                        Add
                      </button>
                    </div>
                  );
                })()}
              </div>

              {/* Activity Logs Section */}
              <div className="mt-4 pt-4 border-t border-gray-200">
                <button
                  onClick={() => setShowActivityLogs(!showActivityLogs)}
                  className="flex items-center justify-between w-full text-sm font-medium text-gray-700 hover:text-gray-900"
                >
                  <span className="flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Activity History {activityLogs.length > 0 && `(${activityLogs.length})`}
                  </span>
                  {showActivityLogs ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>

                {showActivityLogs && (
                  <div className="mt-3 max-h-48 overflow-y-auto">
                    {isLoadingLogs ? (
                      <div className="flex items-center justify-center py-4">
                        <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                        <span className="ml-2 text-sm text-gray-500">Loading activity...</span>
                      </div>
                    ) : activityLogs.length === 0 ? (
                      <p className="text-sm text-gray-400 italic py-2">No activity recorded yet</p>
                    ) : (
                      <div className="space-y-2">
                        {activityLogs.map((log) => (
                          <div key={log.id} className="flex items-start gap-2 p-2 bg-gray-50 rounded text-xs">
                            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-brandColor/10 flex items-center justify-center">
                              <User className="w-3 h-3 text-brandColor" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1">
                                <span className="font-medium text-gray-900">{log.performed_by}</span>
                                <span className="text-gray-400">•</span>
                                <span className="text-gray-500">{formatLogDate(log.created_at)}</span>
                              </div>
                              <p className="text-gray-600 mt-0.5">
                                {formatActivityType(log.activity_type)}
                                {formatActivityData(log.activity_type, log.activity_data) && (
                                  <span className="ml-1 text-gray-800 font-medium">
                                    {formatActivityData(log.activity_type, log.activity_data)}
                                  </span>
                                )}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="flex justify-end gap-2 px-4 py-3 border-t border-gray-200 bg-gray-50 rounded-b-lg">
              <button
                onClick={closeTagModal}
                disabled={isSubmittingTags}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitTags}
                disabled={pendingTags.length === 0 || isSubmittingTags}
                className="px-4 py-2 text-sm font-medium text-white bg-brandColor rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isSubmittingTags ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>Submit {pendingTags.length > 0 && `(${pendingTags.length})`}</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Remark Modal */}
      {remarkModalOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 bg-brandColor rounded-t-lg">
              <h3 className="text-lg font-semibold text-white">
                Add Remark - #ZH{remarkModalOrder.order_number}
              </h3>
              <button
                onClick={closeRemarkModal}
                className="text-white/80 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4">
              {/* Customer Info */}
              <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                <p className="text-sm font-medium text-gray-900">{remarkModalOrder.customer_name}</p>
                <p className="text-xs text-gray-500">{remarkModalOrder.phone}</p>
              </div>

              {/* Remark Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Remark / Note
                </label>
                <textarea
                  value={remarkInput}
                  onChange={(e) => setRemarkInput(e.target.value)}
                  placeholder="Enter remark or note about this order..."
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-brandColor text-sm resize-none"
                  disabled={isAddingRemark}
                  autoFocus
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 px-4 py-3 border-t border-gray-200 bg-gray-50 rounded-b-lg">
              <button
                onClick={closeRemarkModal}
                disabled={isAddingRemark}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAddRemark}
                disabled={!remarkInput.trim() || isAddingRemark}
                className="px-4 py-2 text-sm font-medium text-white bg-brandColor rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isAddingRemark ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Save Remark
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Send WhatsApp Modal */}
      {whatsAppModalOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 bg-green-600 rounded-t-lg">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <MessageCircle className="w-5 h-5" />
                Send WhatsApp - #ZH{whatsAppModalOrder.order_number}
              </h3>
              <button
                onClick={closeWhatsAppModal}
                className="text-white/80 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4">
              {/* Customer Info */}
              <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-gray-500" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">{whatsAppModalOrder.customer_name}</p>
                    <p className="text-xs text-gray-600">{whatsAppModalOrder.phone || 'No phone number'}</p>
                  </div>
                </div>
              </div>

              {!whatsAppModalOrder.phone ? (
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-center">
                  <p className="text-sm text-yellow-800">No phone number available for this order.</p>
                </div>
              ) : (
                <>
                  {/* Template Selection */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select Template
                    </label>
                    <select
                      value={selectedTemplate?.id || ''}
                      onChange={(e) => {
                        const template = whatsAppTemplates.find(t => t.id === e.target.value);
                        setSelectedTemplate(template || null);
                        if (template && whatsAppModalOrder) {
                          setTemplateParams(prefillTemplateParams(template, whatsAppModalOrder));
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green-500 text-sm"
                    >
                      <option value="">Choose a template...</option>
                      {whatsAppTemplates.map(template => (
                        <option key={template.id} value={template.id}>
                          {template.name} - {template.description}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Template Parameters */}
                  {selectedTemplate && (
                    <>
                      <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                        <p className="text-xs font-medium text-green-800 mb-1">Preview:</p>
                        <p className="text-sm text-green-700">{selectedTemplate.sample}</p>
                      </div>

                      <div className="space-y-3 mb-4">
                        {selectedTemplate.parameters.map(param => (
                          <div key={param}>
                            <label className="block text-xs font-medium text-gray-600 mb-1 capitalize">
                              {param.replace(/_/g, ' ')}
                            </label>
                            <input
                              type="text"
                              value={templateParams[param] || ''}
                              onChange={(e) => setTemplateParams(prev => ({ ...prev, [param]: e.target.value }))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green-500 text-sm"
                              placeholder={`Enter ${param.replace(/_/g, ' ')}...`}
                            />
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
            <div className="flex justify-end gap-2 px-4 py-3 border-t border-gray-200 bg-gray-50 rounded-b-lg">
              <button
                onClick={closeWhatsAppModal}
                disabled={isSendingWhatsApp}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSendWhatsApp}
                disabled={!selectedTemplate || isSendingWhatsApp || !whatsAppModalOrder.phone}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isSendingWhatsApp ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Send WhatsApp
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Initiate Call Modal */}
      {callModalOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[80] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Initiating Call</h3>
                <button
                  onClick={closeCallModal}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Customer Information */}
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-brandColor/10 flex items-center justify-center">
                    <Phone className="w-5 h-5 text-brandColor" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{callModalOrder.customer_name}</p>
                    <p className="text-sm text-gray-600">{callModalOrder.phone}</p>
                  </div>
                </div>
              </div>

              {/* Call State */}
              <div className="mb-6">
                {callState === 'initiating' && (
                  <div className="text-center py-8">
                    <Loader2 className="w-12 h-12 text-brandColor animate-spin mx-auto mb-4" />
                    <p className="text-gray-900 font-medium mb-1">Connecting call...</p>
                    <p className="text-sm text-gray-600">Please wait while we connect your call</p>
                  </div>
                )}

                {callState === 'success' && (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                      <CheckCircle className="w-10 h-10 text-green-600" />
                    </div>
                    <p className="text-gray-900 font-medium mb-1">Call initiated successfully!</p>
                    <p className="text-sm text-gray-600">You should receive a call shortly</p>
                    <p className="text-xs text-gray-500 mt-3">Closing in 3 seconds...</p>
                  </div>
                )}

                {callState === 'error' && (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                      <XCircle className="w-10 h-10 text-red-600" />
                    </div>
                    <p className="text-gray-900 font-medium mb-1">Call failed</p>
                    <p className="text-sm text-red-600">{callErrorMessage}</p>
                  </div>
                )}
              </div>

              {/* Actions */}
              {callState === 'error' && (
                <div className="flex items-center justify-end gap-3">
                  <button
                    onClick={closeCallModal}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Close
                  </button>
                  <button
                    onClick={() => initiateCall(callModalOrder)}
                    className="px-4 py-2 text-sm font-medium text-white bg-brandColor rounded-lg hover:opacity-90"
                  >
                    Try Again
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrdersProcessing;
